import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import ArticleDrawer from "./ArticleDrawer";
import ArticleCard from "./ArticleCard";
import Carousel from "../Shared/Carousel";
import "./Articles.css";
import { fetchPosts } from "../../utils/cmsAPI";
import { getArticleSlug } from "../../utils/slugUtils";

const Articles = ({ articleSlug, article, loading: propLoading }) => {
  const navigate = useNavigate();
  const [articlesData, setArticlesData] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Skip if already fetched
    if (hasFetchedRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasFetchedRef.current) {
          hasFetchedRef.current = true;
          const fetchArticles = async () => {
            try {
              const posts = await fetchPosts({ 
                published: true, 
                sort: 'date',
                limit: 10 
              });
              setArticlesData(posts);
            } catch (error) {
              console.error('Failed to fetch articles:', error);
            } finally {
              setLoading(false);
            }
          };
          fetchArticles();
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before section is visible
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []); // Empty dependency array - only run once on mount

  // Open drawer if article prop is provided (from URL)
  useEffect(() => {
    if (article && !propLoading) {
      setSelectedArticle(article);
      setDrawerOpen(true);
    }
  }, [article, propLoading]);

  const handleArticleClick = (article) => {
    // Open drawer immediately, then update URL in background
    setSelectedArticle(article);
    setDrawerOpen(true);
    
    // Update URL without causing navigation/reload
    const slug = getArticleSlug(article);
    window.history.pushState({}, '', `/article/${slug}`);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedArticle(null);
    // Update URL to home when closing drawer if opened from URL
    if (articleSlug || window.location.pathname.startsWith('/article/')) {
      window.history.pushState({}, '', '/');
    }
  };

  return (
    <Box component="section" id="articles" className="articles-section" ref={sectionRef}>
      <Typography variant="h2">Product Perspectives</Typography>
      <Box className="articles-grid">
        {(loading || propLoading) ? (
          <p className="articles-empty">Loading articles...</p>
        ) : articlesData.length > 0 ? (
          <Carousel className="articles-carousel">
            {articlesData.map((article) => (
              <ArticleCard
                key={article.id}
                article={article}
                onClick={() => handleArticleClick(article)}
              />
            ))}
          </Carousel>
        ) : (
          <p className="articles-empty">No articles available. Please add some via the CMS.</p>
        )}
      </Box>
      <ArticleDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        article={selectedArticle}
      />
    </Box>
  );
};

export default Articles;